// src/Dashboard/pages/user/DashboardUser/hooks/useDashboardUserData.js

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaEnvelope,
  FaClipboardList,
  FaBullseye,
  FaStar,
  FaCalendarAlt,
  FaMoneyBillAlt,
  FaTrophy,
} from "react-icons/fa";

import { getNotificationsByUser } from "../../../../../services/notificationServices";
import { getMessagesByUser } from "../../../../../services/messageServices";
import { getAllMessagesContactForm } from "../../../../../services/contactformServices";
import { getUserFavorites } from "../../../../../services/usersTechniquesServices";
import { getUsersEvents } from "../../../../../services/usersEventsServices";
import { getReglementsByUser } from "../../../../../services/achatsServices";
import { getAdherentsByUser } from "../../../../../services/adherentServices";
import { getEventsByAdherent } from "../../../../../services/eventServices";
import { getObjectifsByAdherent } from "../../../../../services/objectifsServices";

import {
  DASHBOARD_USER_ACTIVE_TAB_KEY,
  extractEventId,
  extractTechniqueId,
  getResolvedUserId,
  isPendingReglement,
  isReadFlag,
  uniqueIds,
} from "../utils/dashboardUserUtils";

const INITIAL_COUNTS = {
  payments: { total: 0 },
  messages: { total: 0, unread: 0 },
  notifications: { total: 0, unread: 0 },
  objectifs: { total: 0 },
  competitions: { total: 0 },
  events: { total: 0 },
  techniques: { total: 0 },
  contact: { total: 0, unread: 0 },
};

export default function useDashboardUserData({ user, userRole }) {
  const userId = useMemo(() => getResolvedUserId(user), [user]);
  const isAdminOrTeacher = ["admin", "enseignant"].includes(userRole);

  const [active, setActive] = useState(() => {
    return localStorage.getItem(DASHBOARD_USER_ACTIVE_TAB_KEY) || "payments";
  });

  const [counts, setCounts] = useState(INITIAL_COUNTS);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_USER_ACTIVE_TAB_KEY, active);
  }, [active]);

  const setModuleCounts = useCallback((key, obj) => {
    setCounts((current) => ({
      ...current,
      [key]: {
        ...current[key],
        ...obj,
      },
    }));
  }, []);

  const onCountsPayments = useCallback(
    (obj) => setModuleCounts("payments", obj),
    [setModuleCounts]
  );

  const onCountsMessages = useCallback(
    (obj) => setModuleCounts("messages", obj),
    [setModuleCounts]
  );

  const onCountsNotifications = useCallback(
    (obj) => setModuleCounts("notifications", obj),
    [setModuleCounts]
  );

  const onCountsObjectifs = useCallback(
    (obj) => setModuleCounts("objectifs", obj),
    [setModuleCounts]
  );

  const onCountsCompetitions = useCallback(
    (obj) => setModuleCounts("competitions", obj),
    [setModuleCounts]
  );

  const onCountsEvents = useCallback(
    (obj) => setModuleCounts("events", obj),
    [setModuleCounts]
  );

  const onCountsTechniques = useCallback(
    (obj) => setModuleCounts("techniques", obj),
    [setModuleCounts]
  );

  const onCountsContact = useCallback(
    (obj) => setModuleCounts("contact", obj),
    [setModuleCounts]
  );

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const loadCounts = async () => {
      try {
        const [
          notifications,
          messages,
          contactMessages,
          favoriteTechniques,
          favoriteEvents,
          reglements,
          adherents,
        ] = await Promise.all([
          getNotificationsByUser(userId).catch(() => []),
          getMessagesByUser(userId).catch(() => []),
          isAdminOrTeacher
            ? getAllMessagesContactForm(userId).catch(() => [])
            : Promise.resolve([]),
          getUserFavorites(userId).catch(() => []),
          getUsersEvents(userId).catch(() => []),
          getReglementsByUser(userId).catch(() => []),
          getAdherentsByUser(userId).catch(() => []),
        ]);

        if (cancelled) return;

        const receivedMessages = (messages || []).filter((m) => !m.is_copy);

        const notificationUnread = (notifications || []).reduce(
          (acc, item) => acc + (isReadFlag(item.read_notification) ? 0 : 1),
          0
        );

        const messageUnread = receivedMessages.reduce(
          (acc, item) => acc + (isReadFlag(item.read_message) ? 0 : 1),
          0
        );

        const contactUnread = (contactMessages || []).reduce(
          (acc, item) => acc + (isReadFlag(item.read_message) ? 0 : 1),
          0
        );

        const techniqueIds = uniqueIds(favoriteTechniques, extractTechniqueId);
        const eventIds = uniqueIds(favoriteEvents, extractEventId);
        const pendingPayments = (reglements || []).filter(isPendingReglement);

        let objectifsCount = 0;
        let competitionsCount = 0;

        const adherentList = Array.isArray(adherents) ? adherents : [];

        if (adherentList.length > 0) {
          const objectifsByAdherent = await Promise.all(
            adherentList.map((adherent) =>
              getObjectifsByAdherent(adherent.id).catch(() => [])
            )
          );

          if (!cancelled) {
            objectifsCount = objectifsByAdherent
              .flat()
              .filter((objectif) => !objectif?.is_done).length;
          }

          const eventsByAdherent = await Promise.all(
            adherentList.map((adherent) =>
              getEventsByAdherent(adherent.id).catch(() => null)
            )
          );

          if (!cancelled) {
            competitionsCount = eventsByAdherent.reduce((acc, row) => {
              const events = Array.isArray(row?.Events) ? row.Events : [];
              return acc + events.length;
            }, 0);
          }
        }

        if (cancelled) return;

        setCounts({
          payments: { total: pendingPayments.length },
          messages: {
            total: receivedMessages.length,
            unread: messageUnread,
          },
          notifications: {
            total: (notifications || []).length,
            unread: notificationUnread,
          },
          objectifs: { total: objectifsCount },
          competitions: { total: competitionsCount },
          events: { total: eventIds.length },
          techniques: { total: techniqueIds.length },
          contact: {
            total: (contactMessages || []).length,
            unread: contactUnread,
          },
        });
      } catch (error) {
        console.error("[DashboardUser] loadCounts error:", error);
      }
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [userId, isAdminOrTeacher]);

  const modules = useMemo(
    () =>
      [
        {
          id: "payments",
          title: "Règlements",
          icon: FaMoneyBillAlt,
          badge: counts.payments.total,
          badgeTone: "amber",
          attention: counts.payments.total > 0,
          subtitle: "À effectuer",
        },
        {
          id: "messages",
          title: "Messages",
          icon: FaEnvelope,
          badge: counts.messages.unread,
          badgeTone: "violet",
          attention: counts.messages.unread > 0,
          subtitle: "Non lus",
        },
        {
          id: "notifications",
          title: "Notifications",
          icon: FaBell,
          badge: counts.notifications.unread,
          badgeTone: "rose",
          attention: counts.notifications.unread > 0,
          subtitle: "Non lues",
        },
        {
          id: "objectifs",
          title: "Objectifs",
          icon: FaBullseye,
          badge: counts.objectifs.total,
          badgeTone: "emerald",
          subtitle: "En cours",
        },
        {
          id: "competitions",
          title: "Inscriptions & résultats",
          icon: FaTrophy,
          badge: counts.competitions.total,
          badgeTone: "sky",
          subtitle: "Évènements",
        },
        {
          id: "events",
          title: "Évènements favoris",
          icon: FaCalendarAlt,
          badge: counts.events.total,
          badgeTone: "emerald",
          subtitle: "Favoris",
        },
        {
          id: "techniques",
          title: "Techniques favorites",
          icon: FaStar,
          badge: counts.techniques.total,
          badgeTone: "emerald",
          subtitle: "Favoris",
        },
        isAdminOrTeacher && {
          id: "contact",
          title: "Formulaires",
          icon: FaClipboardList,
          badge: counts.contact.unread,
          badgeTone: "rose",
          attention: counts.contact.unread > 0,
          subtitle: "À traiter",
        },
      ].filter(Boolean),
    [counts, isAdminOrTeacher]
  );

  useEffect(() => {
    if (!modules.length) return;
    if (!modules.some((module) => module.id === active)) {
      setActive(modules[0].id);
    }
  }, [active, modules]);

  return {
    userId,
    active,
    setActive,
    counts,
    modules,
    isAdminOrTeacher,
    onCountsPayments,
    onCountsMessages,
    onCountsNotifications,
    onCountsObjectifs,
    onCountsCompetitions,
    onCountsEvents,
    onCountsTechniques,
    onCountsContact,
  };
}